import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {FormattedMessage, useIntl} from "react-intl";
import {EditableInput, EditButton} from "./index";
import {useState} from "react";
import getTranslated from "../locale/getTranslated";
import FormFieldControl from "./FormFieldControl";
import messages from '@super-template/messages';
import {Controller} from "react-hook-form";
import ReactHtmlParser from 'react-html-parser';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 30,
    '& td, th': {
      paddingTop: 0,
      paddingBottom: 0,
      [theme.breakpoints.down('md')]: {
        paddingLeft: 3,
        paddingRight: 3,
      }
    },
    '& th': {
      verticalAlign: 'bottom'
    },
    '& .full-width': {
      width: '100%'
    },
    '& .sel': {
      textAlignLast: 'center'
    }
  },
  container: {
    overflowX: 'visible'
  },
  mobileScale: {
    display: 'block',
    margin: '10px 0',
    '& strong': {
      paddingRight: 10,
      display: 'inline-block'
    }
  },
  rg: {
    display: 'block'
  },
  row: {
    '&:nth-child(odd)': {
      background: theme.palette.action.hover
    }
  },
  error: {
    color: theme.palette.error.main
  }
}));

export default ({scale, categories, name, isAdmin, control, fieldOptions = {}, validate, fieldError, locale, sizes}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const description = getTranslated(fieldOptions.description, locale, true);
  const title = getTranslated(fieldOptions.title, locale);
  const width = `${100 / (scale.length + 2)}%`;
  if (fieldError) fieldError = !!Object.keys(fieldError).find((key) => categories.find(obj => key === obj.id));

  return editing ? <EditableInput fieldOptions={fieldOptions} close={() => setEditing(false)} id={name}/> :
    <FormFieldControl component="fieldset" fullWidth className={classes.root} required={true} error={fieldError}>
      <Box display="flex" justifyContent="space-between">
        <FormLabel component="legend">{title}</FormLabel>
        {isAdmin && <EditButton onClick={() => setEditing(true)}/>}
      </Box>
      {description && <FormHelperText component="div" id={`_description`} className={'description'}>
        {ReactHtmlParser(description)}
        {sizes.isMobile &&
        <span className={classes.mobileScale}>
          {scale.map(({key, val}) =>
            <strong key={key + "_s"}>{key === '0' ? 'X' : key} = <FormattedMessage {...val}/></strong>)}
        </span>}
      </FormHelperText>}
      <TableContainer className={classes.container}>
        <Table aria-label={title} size="small">
          {!sizes.isTablet &&
          <colgroup>
            <col/>
            {scale.map(s => <col key={s.key + "_col"} style={{width}}/>)}
          </colgroup>}
          {!sizes.isTablet &&
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant={"srOnly"}><FormattedMessage {...messages.subject}/></Typography></TableCell>
              {scale.map(s =>
                <TableCell key={s.key + "_cell"} align="center"><FormattedMessage {...s.val}/></TableCell>)}
            </TableRow>
          </TableHead>}
          <TableBody>
            {categories.map(({category, id, options}) =>
              <Controller
                key={id + "controller"}
                control={control} name={id} defaultValue={''} rules={isAdmin ? {} : {...validate, required: true}}
                render={({fieldState: {error}, field: {ref, ...field}}) =>
                  <TableRow className={classes.row}>
                    <TableCell scope="row" className={!!error ? classes.error : ''}>
                      {intl.formatMessage(category)}
                    </TableCell>
                    {sizes.isTablet ?
                      <MobileOptions id={id} label={intl.formatMessage(category)} field={field} isMobile={sizes.isMobile}
                                     options={scale} inputRef={ref} isAdmin={isAdmin}/> :
                      <DesktopOptions category={category} options={options} isAdmin={isAdmin} r={ref} field={field}/>}
                  </TableRow>}/>)}
          </TableBody>
        </Table>
      </TableContainer>
      {fieldError &&
      <FormHelperText className="MuiFormHelperText-root Mui-error Mui-required">
        <FormattedMessage {...messages.fill_all}/>
      </FormHelperText>}
    </FormFieldControl>
};

const MobileOptions = ({id, title, field, options, inputRef, isMobile, isAdmin}) => (
  <TableCell style={{paddingRight: 0}}>
    <FormControl disabled={isAdmin} innerRef={inputRef} fullWidth style={{textAlign: 'center'}}>
      <Select {...field} inputProps={{'aria-label': title}} style={{minHeight: 50}}>
        {options.map(({key, val}) =>
          <MenuItem key={id + key} style={{justifyContent: 'center', padding: 10}} value={key}>
            {isMobile ? key === '0' ? 'X' : key : <FormattedMessage {...val}/>}
          </MenuItem>)}
      </Select>
    </FormControl>
  </TableCell>
);

const DesktopOptions = ({category, options, field, r, isAdmin}) => {
  const intl = useIntl();
  const classes = useStyles();
  return (
    <>
      {options.map(o =>
        <TableCell align="center" key={`${o.name}_${o.value}`}>
          <RadioGroup key={`${o.name}_${o.value}_rg`} required {...field}
                      aria-label={intl.formatMessage(category)} className={classes.rg}>
            <Radio inputProps={{'aria-label': intl.formatMessage(o.label)}} value={o.value} inputRef={r}
                   disabled={isAdmin}/>
          </RadioGroup>
        </TableCell>)}
    </>
  )
};
